import { Controller, useForm } from 'react-hook-form'
import { salesforceSettingsDefaultValues, SalesforceSettingsFormValues } from './models'
import React, { useEffect } from 'react'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../components/Button/Button'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { useSalesforceBasedContactSync } from './hooks'

export interface ContactSyncSettingsProps {
  serviceId: string
}

export const ContactSyncSettings: React.FC<ContactSyncSettingsProps> = ({ serviceId }) => {
  const { autoSync, isSyncDisabled, isAccountFetching, isFetching, isSaving, save } = useSalesforceBasedContactSync(serviceId as IntegrationServiceId)

  const form = useForm<SalesforceSettingsFormValues>({ defaultValues: salesforceSettingsDefaultValues })

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  const handleCancel = () => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }

  const onSubmit = async (data: SalesforceSettingsFormValues) => {
    await save(data)
  }

  if (isFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and {getIntegrationDisplayName(serviceId as IntegrationServiceId)}.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isSaving}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
